/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ChainedCommands, Editor } from "@tiptap/react";
import BoldIcon from "../../../assets/icon/CampaignNotesToolbar/bold-icon";
import ItalicIcon from "../../../assets/icon/CampaignNotesToolbar/italic-icon";
import StrikethroughIcon from "../../../assets/icon/CampaignNotesToolbar/strikethrough-icon";
import UnderlineIcon from "../../../assets/icon/CampaignNotesToolbar/underline-icon";
import { notSelect } from "../../../styles/general";
import { getSelectedAnchorElement } from "../../../utils/LinkUtils";
import { BsCheckLg } from "react-icons/bs";

const Container = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  cursor: auto;

  left: -5px;
  top: 50px;

  width: 195px;

  background-color: #fff;
  border: 1px solid #ededed;
  border-radius: 4px;

  z-index: 99991;

  box-shadow: 0px 2px 5px #00000026;
  color: #333333 !important;

  :after {
    content: "";
    position: absolute;
    display: block;
    border-style: solid;
    border-color: #fff transparent;

    border-width: 9px 7px 0;
    top: -9px;
    left: 14px;
    transform: rotate(180deg);
    width: 0;
    z-index: 1;
  }

  :before {
    content: "";
    position: absolute;
    border-style: solid;
    border-color: #ededed transparent;
    display: block;

    border-width: 10px 8px 0;
    top: -10px;
    left: 13px;
    transform: rotate(180deg) translateY(0.5px);
    width: 0;
    z-index: 1;
  }
`;

const TopbarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 50px;

  border-bottom: 1px solid #ededed;
`;

const ItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 135px;
  height: 26px;
`;

const TopbarItem = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${notSelect}

  width: 26px;
  height: 26px;

  border-radius: 5px;
  background-color: ${(props) => (props.isActive ? "#fb972e" : "fff")};
  path,
  rect {
    fill: ${(props) => (props.isActive ? "white" : "black")};
  }

  &:hover {
    background-color: #fb972e;

    path,
    rect {
      fill: white !important;
    }
  }
`;

const ChecklistContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  padding: 10px;
  width: 100%;
`;

const ChecklistItem = styled.div<{ isActive?: boolean }>`
  display: flex;
  font-weight: bold;
  align-items: center;
  cursor: pointer;

  .icon {
    display: ${(props) => (!props.isActive ? "none" : "auto")};
    position: absolute;
    width: 15px;
    height: 15px;
    margin-left: -30px;
    margin-top: 0px;
  }

  width: 100%;
  height: 30px;
  margin-bottom: 1px;
  padding-left: 37.2px;
  ${notSelect}

  &.h1 {
    font-size: 21px;
  }
  &.h2 {
    font-size: 18px;
  }
  &.h3 {
    font-size: 15px;
  }
  &.p {
    font-size: 15px;
    font-weight: initial;
  }

  &:hover {
    background-color: #fb972e;
    border-radius: 4px;
    color: #fff;
  }
`;

const StyledListContainer = styled.div<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  ${notSelect}
  width: 100%;
  height: 30px;
  padding-left: 37.2px;
  position: relative;

  .icon {
    display: ${(props) => (!props.isActive ? "none" : "auto")};
    position: absolute;
    width: 15px;
    height: 15px;
    margin-left: -30px;
    margin-top: 0px;
  }

  &:hover {
    background-color: #fb972e;
    border-radius: 4px;
    color: #fff;
  }

  > :is(ul, ol) {
    padding-left: 20px;
  }
`;

interface INotePopoverFormatting {
  editor: Editor;
}

export default function NotePopoverFormatting({
  editor,
}: INotePopoverFormatting) {
  function editorChainKeepAlign(editor: Editor, chain: ChainedCommands) {
    // Heading resets alignment, so I need to do that too
    if (editor.isActive({ textAlign: "left" })) chain.setTextAlign("left");
    else if (editor.isActive({ textAlign: "right" }))
      chain.setTextAlign("right");
    else if (editor.isActive({ textAlign: "center" }))
      chain.setTextAlign("center");
  }

  function handleBold() {
    const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

    if (selectedAnchor) {
      const boldByStyle =
        selectedAnchor.style.fontWeight &&
        /^(bold(er)?|[5-9]\d{2,})$/.test(
          selectedAnchor.style.fontWeight as string
        );

      let boldByDecoration: any = selectedAnchor.parentNode;
      let isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent

      if (!isBoldByDecoration) {
        boldByDecoration = boldByDecoration.parentNode;
        isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
      }

      if (!isBoldByDecoration) {
        boldByDecoration = boldByDecoration.parentNode;
        isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
      }

      if (isBoldByDecoration && boldByStyle) {
        selectedAnchor.style.fontWeight = "none !important";
        editor.chain().unsetBold().run();
      } else if (isBoldByDecoration) {
        editor.chain().unsetBold().run();
      } else if (boldByStyle) {
        selectedAnchor.style.fontWeight = "none !important";
        editor.chain().unsetBold().run();
      } else {
        editor.chain().toggleBold().run();
      }
    } else {
      editor.chain().toggleBold().run();
    }
    return true;
  }

  function handleItalic() {
    const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

    if (selectedAnchor) {
      const italicByStyle = selectedAnchor.style.fontStyle === "italic";

      let italicByDecoration: any = selectedAnchor.parentNode;
      let isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent

      if (!isItalicByDecoration) {
        italicByDecoration = italicByDecoration.parentNode;
        isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent
      }

      if (isItalicByDecoration && italicByStyle) {
        selectedAnchor.style.fontStyle = "none !important";
        editor.chain().unsetItalic().run();
      } else if (isItalicByDecoration) {
        editor.chain().unsetItalic().run();
      } else if (italicByStyle) {
        selectedAnchor.style.fontStyle = "none !important";
        editor.chain().unsetItalic().run();
      } else {
        editor.chain().toggleItalic().run();
      }
    } else {
      editor.chain().toggleItalic().run();
    }
    return true;
  }

  function handleUnderline() {
    const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

    if (selectedAnchor) {
      const underlinedByStyle =
        selectedAnchor.style.textDecoration === "underline";

      let underlinedByDecoration: any = selectedAnchor.parentNode;
      let isUnderlinedByDecoration = underlinedByDecoration.nodeName === "U"; // replace with direct parent

      if (isUnderlinedByDecoration && underlinedByStyle) {
        selectedAnchor.style.textDecoration = "none !important";
        editor.chain().unsetUnderline().run();
      } else if (isUnderlinedByDecoration) {
        editor.chain().unsetUnderline().run();
      } else if (underlinedByStyle) {
        selectedAnchor.style.textDecoration = "none !important";
        editor.chain().unsetUnderline().run();
      } else {
        editor.chain().toggleUnderline().run();
      }
    } else {
      editor.chain().toggleUnderline().run();
    }
    return true;
  }

  function handleStrikethrough() {
    const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

    if (selectedAnchor) {
      const strikedByStyle =
        selectedAnchor.style.textDecoration === "line-through";

      let strikedByDecoration: any = selectedAnchor.parentNode;
      let isStrikedByDecoration = strikedByDecoration.nodeName === "S"; // replace with direct parent

      if (isStrikedByDecoration && strikedByStyle) {
        selectedAnchor.style.textDecoration = "none !important";
        editor.chain().unsetStrike().run();
      } else if (isStrikedByDecoration) {
        editor.chain().unsetStrike().run();
      } else if (strikedByStyle) {
        selectedAnchor.style.textDecoration = "none !important";
        editor.chain().unsetStrike().run();
      } else {
        editor.chain().toggleStrike().run();
      }
    } else {
      editor.chain().toggleStrike().run();
    }
    return true;
  }

  function handleH1() {
    let chain = editor.chain().toggleHeading({ level: 1 });
    editorChainKeepAlign(editor, chain);
    chain.run();
  }

  function handleH2() {
    let chain = editor.chain().toggleHeading({ level: 2 });
    editorChainKeepAlign(editor, chain);
    chain.run();
  }

  function handleH3() {
    let chain = editor.chain().toggleHeading({ level: 3 });
    editorChainKeepAlign(editor, chain);
    chain.run();
  }

  function handleParagraph() {
    let chain = editor.chain().setParagraph();
    editorChainKeepAlign(editor, chain);
    chain.run();
  }

  function convertStyleNameToNodeName(
    type: "bold" | "italic" | "underline" | "strike"
  ): string {
    switch (type) {
      case "bold":
        return "STRONG";
      case "italic":
        return "EM";
      case "underline":
        return "U";
      case "strike":
        return "S";
      default:
        return "";
    }
  }

  /**
   * Function that returns if a given selection, or block within the editor,
   * is active.
   *
   * Has a special case when the selection is a link.
   * @param type
   * @returns
   */
  function isElementActive(
    type: "bold" | "italic" | "underline" | "strike"
  ): boolean {
    // Check if my selection is an anchor
    const selectedAnchor: HTMLAnchorElement | null = getSelectedAnchorElement();

    if (!selectedAnchor) {
      // If it's not, default behavior, use tiptap to get if the element is active

      return editor.isActive(type);
    }

    // Else, go through the anchor element, walking through the HTML tree
    // inside out to check for <u>, <strong>, or inline styles :)

    let typeByStyle: boolean;
    let isTypeByDecoration: boolean;
    let typeByDecoration = selectedAnchor.parentNode;
    let currentNode = typeByDecoration;

    let parentsList: string[] = [];
    let count = 0;

    while (currentNode?.nodeName !== "P") {
      parentsList.push(currentNode.nodeName);
      currentNode = currentNode.parentNode;
      if (count === 5) break;
      count++;
    }

    if (type === "bold") {
      typeByStyle =
        selectedAnchor.style.fontWeight &&
        /^(bold(er)?|[5-9]\d{2,})$/.test(
          selectedAnchor.style.fontWeight as string
        );
    } else typeByStyle = selectedAnchor.style.textDecoration === type;

    // console.log("[PALHARI]", "parents list", parentsList);

    isTypeByDecoration = parentsList.includes(convertStyleNameToNodeName(type));

    // console.log("[PALHARI]", "1", typeByStyle || isTypeByDecoration);
    return typeByStyle || isTypeByDecoration;
  }

  return (
    <Container>
      <TopbarContainer>
        <ItemContainer>
          <TopbarItem
            onClick={() => {
              if (!editor.isFocused) return;
              handleBold();
            }}
            isActive={isElementActive("bold")}
          >
            <BoldIcon />
          </TopbarItem>
          <TopbarItem
            onClick={() => {
              if (!editor.isFocused) return;
              handleItalic();
            }}
            isActive={isElementActive("italic")}
          >
            <ItalicIcon />
          </TopbarItem>
          <TopbarItem
            onClick={() => {
              if (!editor.isFocused) return;
              handleUnderline();
            }}
            isActive={isElementActive("underline")}
          >
            <UnderlineIcon />
          </TopbarItem>
          <TopbarItem
            onClick={() => {
              if (!editor.isFocused) return;
              handleStrikethrough();
            }}
            isActive={isElementActive("strike")}
          >
            <StrikethroughIcon />
          </TopbarItem>
        </ItemContainer>
      </TopbarContainer>
      <ChecklistContainer>
        <ChecklistItem
          className="h1"
          onClick={() => {
            if (!editor.isFocused) return;
            handleH1();
          }}
          isActive={editor.isActive("heading", { level: 1 })}
        >
          <BsCheckLg className="icon" />
          Title {"<h1>"}
        </ChecklistItem>

        <ChecklistItem
          className="h2"
          onClick={() => {
            if (!editor.isFocused) return;
            handleH2();
          }}
          isActive={editor.isActive("heading", { level: 2 })}
        >
          <BsCheckLg className="icon" />
          Heading {"<h2>"}
        </ChecklistItem>
        <ChecklistItem
          className="h3"
          onClick={() => {
            if (!editor.isFocused) return;
            handleH3();
          }}
          isActive={editor.isActive("heading", { level: 3 })}
        >
          <BsCheckLg className="icon" />
          Subheading {"<h3>"}
        </ChecklistItem>
        <ChecklistItem
          className="p"
          onClick={() => {
            if (!editor.isFocused) return;
            handleParagraph();
          }}
          isActive={editor.isActive("paragraph")}
        >
          <BsCheckLg className="icon" />
          Body
        </ChecklistItem>
        <StyledListContainer
          isActive={editor.isActive("bulletList")}
          onClick={() => {
            if (!editor.isFocused) return;
            editor.chain().toggleBulletList().run();
          }}
        >
          <BsCheckLg className="icon" />
          <ul>
            <li>Bulleted List</li>
          </ul>
        </StyledListContainer>
        {/* <StyledListContainer
          isActive={editor.isActive("dashedList")}
          onClick={() => {
            editor.chain().focus().toggleDashedList().run();
          }}
        >
          <BsCheckLg className="icon" />
          <ul
            css={css`
              list-style-type: none;
              > li::before {
                content: "_";
                position: absolute;
                margin-left: -18px;
                margin-top: -6px;
              }
            `}
          >
            <li>Dashed List</li>
          </ul>
        </StyledListContainer> */}
        <StyledListContainer
          isActive={editor.isActive("orderedList")}
          onClick={() => {
            if (!editor.isFocused) return;
            editor.chain().toggleOrderedList().run();
          }}
        >
          <BsCheckLg className="icon" />
          <ol style={{ marginLeft: "-5px" }}>
            <li>Numbered List</li>
          </ol>
        </StyledListContainer>
      </ChecklistContainer>
    </Container>
  );
}
